import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import {
  Table,
  Tag,
  Button,
  Select,
  Modal,
  message,
  Dropdown,
  Menu,
  Checkbox,
} from 'antd';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { ActionMenuIcon, EditAppointmentIcon } from 'src/icons';
import {
  listAllTemplatesAction,
  updateTemplateSpecialtiesAction,
} from 'src/modules/SuperAdmin/store/actions/prebuilttemplates.action';
import { getAllSpecialtyListAction } from 'src/modules/SuperAdmin/store/actions/specialty.action';
import { SuperAdminTable } from '../../../baseComponents/Table';
import { SingleSelect } from '../../../baseComponents';

const EncounterPhaseFilter = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}) => {
  const [keys, setKeys] = useState(selectedKeys || []);

  useEffect(() => {
    setKeys(selectedKeys || []);
  }, [selectedKeys]);

  const handleChange = (checked, value) => {
    const nextKeys = checked
      ? [...keys, value]
      : keys.filter((key) => key !== value);
    setKeys(nextKeys);
  };

  return (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <div className="flex flex-col">
        {[
          { text: 'pre-chart', value: 'pre-chart' },
          { text: 'in-visit', value: 'in-visit' },
          { text: 'after-visit', value: 'after-visit' },
        ].map((option) => (
          <Checkbox
            key={option.value}
            checked={keys.includes(option.value)}
            onChange={(e) => handleChange(e.target.checked, option.value)}
            className="mb-2 ml-0"
          >
            {option.text}
          </Checkbox>
        ))}
      </div>
      <div className="flex justify-between gap-2 mt-2">
        <Button
          size="small"
          onClick={() => {
            clearFilters();
            setKeys([]);
            confirm();
          }}
          className="text-xs"
        >
          Reset
        </Button>
        <Button
          type="primary"
          size="small"
          onClick={() => {
            setSelectedKeys(keys);
            confirm();
          }}
          className="text-xs bg-[#00D090] hover:bg-[#059669]"
        >
          OK
        </Button>
      </div>
    </div>
  );
};

const TemplateSpecialties = () => {
  const dispatch = useDispatch();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [specialtyMap, setSpecialtyMap] = useState({});

  const { allTemplates, preBuiltTemplatesLoading } = useSelector(
    (state) => state.preBuiltTemplatesState
  );
  const { getAllSpecialtiesList } = useSelector(
    (state) => state.specialtyState
  );

  useEffect(() => {
    dispatch(listAllTemplatesAction());
    dispatch(getAllSpecialtyListAction());
  }, [dispatch]);

  useEffect(() => {
    if (getAllSpecialtiesList && Array.isArray(getAllSpecialtiesList)) {
      const map = {};
      getAllSpecialtiesList.forEach((specialty) => {
        map[specialty.specialty_id] = specialty.specialty_name;
      });
      setSpecialtyMap(map);
    }
  }, [getAllSpecialtiesList]);

  useEffect(() => {
    if (allTemplates && Array.isArray(allTemplates)) {
      console.log('specialtyMap', specialtyMap);
      const processedTemplates = allTemplates.map((template, index) => ({
        key: index,
        template_id: template.template_id,
        name: template.name,
        specialty_ids: template.specialty_ids || [],
        encounter_phase: template.encounter_phase,
        specialty_names: (template.specialty_ids || [])
          .map((id) => specialtyMap[id])
          .filter(Boolean),
      }));
      setTemplates(processedTemplates);
    }
  }, [allTemplates, specialtyMap]);

  const handleEditSpecialty = (template) => {
    setCurrentTemplate(template);
    setEditModalOpen(true);
  };

  const handleSaveSpecialties = async (values) => {
    if (!currentTemplate) return;

    const success = await dispatch(
      updateTemplateSpecialtiesAction(
        currentTemplate.template_id,
        values.selectedSpecialties || []
      )
    );

    if (success) {
      setEditModalOpen(false);
      setCurrentTemplate(null);
    }
  };

  const handleCancel = () => {
    setEditModalOpen(false);
    setCurrentTemplate(null);
  };

  const handleRefresh = () => {
    dispatch(listAllTemplatesAction());
    dispatch(getAllSpecialtyListAction());
  };

  const menu_options = (record) => {
    return (
      <Menu
        onClick={({ key }) => {
          if (key === 'editSpecialties') {
            handleEditSpecialty(record);
          }
        }}
      >
        <Menu.Item key="editSpecialties">
          <div className="dropdown-text flex items-center space-x-2">
            <EditAppointmentIcon /> <span>Edit specialties</span>
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  const columns = [
    {
      title: 'Template Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span className="font-medium">{text}</span>,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Encounter Phase',
      dataIndex: 'encounter_phase',
      key: 'encounter_phase',
      filterDropdown: (props) => <EncounterPhaseFilter {...props} />,
      onFilter: (value, record) => record.encounter_phase === value,
    },
    {
      title: 'Specialties',
      dataIndex: 'specialty_names',
      key: 'specialty_names',
      render: (specialtyNames) => (
        <div className="flex flex-wrap gap-2">
          {specialtyNames && specialtyNames.length > 0 ? (
            specialtyNames.map((name, index) => (
              <div
                key={index}
                className="flex items-center px-3 py-1 w-fit rounded-3xl text-sm"
                style={{ border: `1px solid #00D090`, color: '#00D090' }}
              >
                {name}
              </div>
            ))
          ) : (
            <span className="text-gray-400 italic">
              No specialties assigned
            </span>
          )}
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      dataIndex: 'actions',
      render: (text, record) => {
        return (
          <Dropdown className="" overlay={menu_options(record)}>
            <a className="" onClick={(e) => e.preventDefault()}>
              <ActionMenuIcon />
            </a>
          </Dropdown>
        );
      },
    },
  ];

  const specialtyOptions =
    getAllSpecialtiesList?.map((specialty) => ({
      label: specialty.specialty_name,
      value: specialty.specialty_id,
    })) || [];

  const initialValues = {
    selectedSpecialties: currentTemplate?.specialty_ids || [],
  };

  return (
    <div className="pb-4">
      <div className="super-admin-table">
        <div className="mt-5 ant-table rounded-xl shadow-xl">
          <SuperAdminTable
            className="appointment-page-table"
            columns={columns}
            data={templates}
            loading={preBuiltTemplatesLoading}
          />
        </div>
      </div>

      <Modal
        title={`Edit Specialties - ${currentTemplate?.name}`}
        open={editModalOpen}
        onCancel={handleCancel}
        width={600}
        centered={true}
        closeIcon={false}
        className="template-specialties-modal"
        footer={null}
      >
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          onSubmit={handleSaveSpecialties}
        >
          {({ values, setFieldValue, resetForm }) => (
            <Form>
              <div className="flex flex-col h-full p-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Specialties
                  </label>
                  <p className="text-sm text-gray-500 mb-4">
                    Choose one or more specialties for this template
                  </p>
                  <SingleSelect
                    name="selectedSpecialties"
                    options={specialtyOptions}
                    placeholder="Select specialties..."
                    value={values.selectedSpecialties}
                    onChange={(value) =>
                      setFieldValue('selectedSpecialties', value)
                    }
                    dropdownMode="multiple"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 p-4 pt-0">
                <button
                  type="button"
                  className="bg-[#A9A9A9] hover:bg-[#899499] text-white font-normal p-1 py-1 px-2 rounded-md focus:outline-none focus:shadow-outline"
                  onClick={() => {
                    resetForm();
                    handleCancel();
                  }}
                  disabled={preBuiltTemplatesLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-[#00D090] hover:bg-[#059669] text-white font-normal p-1 py-1 px-2 rounded-md focus:outline-none focus:shadow-outline"
                  disabled={preBuiltTemplatesLoading}
                >
                  {preBuiltTemplatesLoading ? 'Saving...' : 'Save Specialties'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default TemplateSpecialties;
